/**
 * GP-1234: one source of truth for an order's FULFILLMENT type (pickup vs
 * delivery) wherever it is printed or previewed.
 *
 * Two different order fields are easy to confuse:
 *  - `deliveryMethod` — 'pickup' | 'delivery'. THE fulfillment type. This is
 *    what the Patient 360 → Order History "Delivery" column renders.
 *  - `deliveryType`   — 'InHouse' | 'Courier' | '3rd_Party' | … The courier
 *    ROUTING for a delivery. It is normally null on a pickup order, which is
 *    why the delivery label printed nothing at all for pickups.
 *
 * The label must read the same field the grid reads so the two can't drift.
 *
 * No silent fallback: an unknown/missing `deliveryMethod` returns null and
 * callers OMIT the indicator. Defaulting to DELIVERY would print a confident
 * wrong answer on a pickup bag — exactly the mis-sort this ticket is about.
 */
export type FulfillmentType = 'PICKUP' | 'DELIVERY';

/** Normalize `order.deliveryMethod` to the printed badge text, or null if unknown. */
export function resolveFulfillmentType(deliveryMethod?: string | null): FulfillmentType | null {
  switch ((deliveryMethod ?? '').trim().toLowerCase()) {
    case 'pickup':
      return 'PICKUP';
    case 'delivery':
      return 'DELIVERY';
    default:
      return null;
  }
}

/**
 * Heading/tab wording for the label: "Pickup Label" for pickups, otherwise
 * "Delivery Label" (also the wording when the method is unknown — the tab
 * always needs a name; it is the badge, not the heading, that stays silent).
 */
export function fulfillmentLabelHeading(deliveryMethod?: string | null): string {
  return resolveFulfillmentType(deliveryMethod) === 'PICKUP' ? 'Pickup Label' : 'Delivery Label';
}
